import { useNavigate } from "react-router";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { useCallback, useMemo, useState } from "react";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { processError } from "../../../../common/utils/base_error";
import { useObrasTopHeaderState } from "../../../obras/presentation/components/hooks/obras_top_header_state";

import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import TaskAltIcon from '@mui/icons-material/TaskAlt'; 
import ShieldIcon from '@mui/icons-material/Shield'; 
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser'; 
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import EventBusyIcon from '@mui/icons-material/EventBusy'; 
import NotificationImportantIcon from '@mui/icons-material/NotificationImportant';
import CommonConstants from "../../../../common/constants";
import { useAuditoresPagination } from "../../../auditoria/presentation/hooks/Auditoria_auditores/pagination";
import { PrincipalDocumentosLista } from "../../../auditoria/domain/models/documentos_lista";
import { QualidadeIndicadores } from "../../domain/models/qualidade_indicadores";

type LoadPrincipalListaFuncState = {
  IdArquivoModulo?: number;
  page?: number;
  rowsPerPage?: number;
  tipoPesquisa?: number,
  pesquisa?: string,
};

export const useQualidadePrincipalViewModel = () => {
  const {
    commons: { getAllListagemDocUseCase, getGedArquivoDataUseCase},
    reports: {},
    qualidade: { getAllIndicadoresUseCase },
  } = useDependencyInjection();


  const [loading, setLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>();
  const [docLista, setDocLista] = useState<PrincipalDocumentosLista[]>([]);
  const [indicadores, setIndicadores] = useState<QualidadeIndicadores | null>(null);
  const navigate = useNavigate();



  const {pushErrorNotification } = usePageLoader();

  const obrasTopHeaderState = useObrasTopHeaderState();
   const pagination = useAuditoresPagination();

  // #region Funções de carregamento

  const loadIndicadoresState = useCallback(async () => {
    try {
        const response =await getAllIndicadoresUseCase.getAllIndicadores();
        setIndicadores(response);
    } catch (e: unknown) {
        throw new Error("Falha ao buscar indicadores!");
    }
  }, [getAllIndicadoresUseCase]);

  const loadDocListaState = useCallback(async (funcState?: LoadPrincipalListaFuncState) => {
    try {
        const filteredPage = funcState?.page ?? pagination.page;
        const filteredRowsPerPage = funcState?.rowsPerPage ?? pagination.rowsPerPage;
        await getAllListagemDocUseCase.getAllListagemDoc(
          String(CommonConstants.moduloDocumentos.Qualidade),
            filteredPage,
            filteredRowsPerPage
        );
        setDocLista(getAllListagemDocUseCase.items);
        pagination.setRowsPerPage(getAllListagemDocUseCase.pageSize);
        pagination.setTotalItems(getAllListagemDocUseCase.totalItems);
    } catch (e: unknown) {
        throw new Error("Falha ao buscar documentos!");
    }
  }, [getAllListagemDocUseCase, pagination]);


  //#endregion
  // #region useEffect
  usePageStartBootstrap(async () => {
    try {
      const jobs = [];

      jobs.push(loadDocListaState());
      jobs.push(loadIndicadoresState());
      await Promise.all(jobs);
    } catch (e: unknown) {
      setErrorMessage(processError(e, 'Falha ao buscar dados da pagina!')[0]);
    }
  });

  const handleReload = useCallback(async () => {
    try {
      const jobs = [];
      setLoading(true);
      jobs.push(loadDocListaState());
      jobs.push(loadIndicadoresState());
      await Promise.all(jobs);
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [loadDocListaState, loadIndicadoresState, pushErrorNotification]);
  //#endregion

  // #region Eventos
  const handleDownloadFile = useCallback(async (IdArquivoModulo: number, NomeArquivo: string) => {
    if (IdArquivoModulo > 0) {
      try {
        setLoading(true);
        await getGedArquivoDataUseCase
          .getGedArquivoData(IdArquivoModulo, NomeArquivo);
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, 'Falha ao buscar documentos!'));
      } finally {
        setLoading(false);
      }
    }
  }, [getGedArquivoDataUseCase, pushErrorNotification]);
  

  const handlePageChange = useCallback(async (page: number) => {
    try {
      setLoading(true);
      pagination.setPage(page + 1);;
      await loadDocListaState({page: page + 1,});
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [loadDocListaState, pagination, pushErrorNotification]);


  const handleRowsPerPageChange = useCallback(async (rowsPerPage: number) => {
  try {
      setLoading(true);
      pagination.setPage(1);
      pagination.setRowsPerPage(rowsPerPage);

      await loadDocListaState({ page: 1, rowsPerPage });
  } catch (e: unknown) {
    pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
  } finally {
    setLoading(false);
  }
}, [loadDocListaState, pagination, pushErrorNotification]);


  //#endregion


  const handleNavegate = useCallback((id: number) => {
      if (id === 1)  navigate(`/AcoesCorretivas_lista/1`)
      else if (id === 2) navigate(`/AcoesCorretivas_lista/2`)
      else if (id === 3) navigate(`/AcoesPreventivas_lista/1`)
      else if (id === 4) navigate(`/AcoesPreventivas_lista/2`)
      else if (id === 5) navigate(`/CronogramaSGQ_lista/1`)
      else if (id === 6) navigate(`/CronogramaSGQ_lista/2`)
      else if (id === 7) navigate(`/AcoesCorretivas_lista/`)
  }, [navigate]);

  const stats = useMemo(() => [
    { 
      title: 'Ações Corretivas Abertas', 
      value: indicadores?.acoesCorretivasAbertas ?? 0, 
      icon: ReportProblemIcon, 
      colorClass: { bg: 'bg-red-100', text: 'text-red-600', bgHover: 'hover:bg-red-200' },
      linkText: 'Ir para cadastro',
      onClick: () => handleNavegate(1)
    },
    { 
      title: 'Ações Corretivas Fechadas', 
      value: indicadores?.acoesCorretivasFechadas ?? 0, 
      icon: TaskAltIcon,
      colorClass: { bg: 'bg-gray-100', text: 'text-gray-600', bgHover: 'hover:bg-gray-200' },
      linkText: 'Ir para cadastro',
      onClick: () => handleNavegate(2)
    },
    { 
      title: 'Ações Preventivas Abertas', 
      value: indicadores?.acoesPreventivasAbertas ??  0, 
      icon: ShieldIcon,
      colorClass: { bg: 'bg-teal-100', text: 'text-teal-600', bgHover: 'hover:bg-teal-200' },
      linkText: 'Ir para cadastro', 
      onClick: () => handleNavegate(3)
    },
    { 
      title: 'Ações Preventivas Fechadas', 
      value: indicadores?.acoesPreventivasFechadas ?? 0, 
      icon: VerifiedUserIcon,
      colorClass: { bg: 'bg-teal-100', text: 'text-teal-600', bgHover: 'hover:bg-teal-200' },
      linkText: 'Ir para cadastro', 
      onClick: () => handleNavegate(4)
    },
    { 
      title: 'Cronogramas SGQ Dentro do Prazo', 
      value: indicadores?.cronogramasDentroDoPrazo ??  0, 
      icon: EventAvailableIcon,
      colorClass: { bg: 'bg-blue-100', text: 'text-blue-600', bgHover: 'hover:bg-blue-200' },
      linkText: 'Ir para cadastro', 
      onClick: () => handleNavegate(5)
    },
    { 
      title: 'Cronogramas SGQ Em Atraso', 
      value: indicadores?.cronogramasEmAtraso ?? 0, 
      icon: EventBusyIcon,
      colorClass: { bg: 'bg-yellow-100', text: 'text-yellow-600', bgHover: 'hover:bg-yellow-200' },
      linkText: 'Ir para cadastro', 
      onClick: () => handleNavegate(6)
    },
    { 
      title: 'Ações Corretivas Em Atraso', 
      value: indicadores?.acoesCorretivasEmAtraso ?? 0, 
      icon: NotificationImportantIcon,
      colorClass: { bg: 'bg-orange-100', text: 'text-orange-600', bgHover: 'hover:bg-orange-200' },
      linkText: 'Ir para cadastro', 
      onClick: () => handleNavegate(7)
    },
  ], [handleNavegate, indicadores?.acoesCorretivasAbertas, indicadores?.acoesCorretivasEmAtraso, indicadores?.acoesCorretivasFechadas, indicadores?.acoesPreventivasAbertas, indicadores?.acoesPreventivasFechadas, indicadores?.cronogramasDentroDoPrazo, indicadores?.cronogramasEmAtraso]);

  return {
      loading,
      errorMessage,
      obrasTopHeaderState,
      docLista,
      pagination,
      stats,
      handleNavegate,
      handleReload,
      handlePageChange,
      handleRowsPerPageChange,
      handleDownloadFile,
  }
}